import { selMessage } from "@selector/shared";
import { uniq } from "ramda";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { destroy } from "redux-form";

import NfGenomeSelectionScreen from "@/ui-modules/playbooks/ngs/NfGenomeSelectionScreen.tsx";
import NfPlaybookProcessorParamsScreen from "@/ui-modules/playbooks/ngs/NfPlaybookProcessorParamsScreen.tsx";

import { getIconElementForCustomPlaybook, type IPlaybookPage } from "../../../app-utils/playbook.js";
import { getPrimaryParentType } from "../../../app-utils/sample.js";
import { type ParentType, type Playbook } from "../../../graphql.js";
import {
  clearNfPlaybookExecutionData,
  initNfPlaybookExecutionState,
  loadNfPlaybook,
} from "../../../redux/modules/nf-playbook-execution.js";
import {
  clearSampleEditingData,
  type ISampleEditingState,
  loadSampleTypeDataForPlaybook,
} from "../../../redux/modules/sample-editing.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import {
  NF_PLAYBOOK_EXECUTION_SCREENS,
  NF_PLAYBOOK_SCREEN_IDS,
  PLAYBOOK_CLASS,
  PLAYBOOK_TYPE,
} from "../../../settings/settings.js";
import PlaybookWizard from "../PlaybookWizard.js";
import { type IWizardStep } from "../PlaybookWizardStep.js";
import NfPlaybookBasicsScreen from "./NfPlaybookBasicsScreen.js";
import NfPlaybookReviewScreen from "./NfPlaybookReviewScreen.js";
import NfPlaybookSamplesScreen from "./NfPlaybookSamplesScreen.js";
import NfPlaybookWorkflowParamsScreen from "./NfPlaybookWorkflowParamsScreen.js";

interface INfPlaybookWizardProps {
  onClose: () => void;
}

const NfPlaybookWizard: FC<INfPlaybookWizardProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const [isInitializingPlaybook, setInitializingPlaybook] = useState<boolean>(true);
  const [configurationError, setConfigurationError] = useState<string>(null);
  const selectedPlaybookId = useSelector<IAppState, number>(
    (state) => state.nfPlaybookExecution.selectedPlaybookId,
    shallowEqual,
  );
  const playbook = useSelector<IAppState, Playbook>((state) => state.nfPlaybookExecution.playbook, shallowEqual);

  useEffect(() => {
    dispatch(loadNfPlaybook(selectedPlaybookId))
      .then((playbook) => {
        // the workflow may be missing, e.g. if an activity of the workflow does not exist any more
        if (playbook.p2Config && !playbook.p2Config?.workflow) {
          throw new Error("Workflow configuration is missing or invalid");
        }
        return dispatch(initNfPlaybookExecutionState()).then(() => playbook);
      })
      .then((playbook) => {
        if (playbook.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES) {
          return loadRelatedSampleType(playbook);
        }
        return;
      })
      .then(() => {
        setInitializingPlaybook(false);
      })
      .catch((error: Error) => {
        setInitializingPlaybook(false);
        setConfigurationError(error.message);
        console.error(error);
      });
  }, [selectedPlaybookId]);

  const loadRelatedSampleType = useCallback((playbook: Playbook) => {
    const mandatoryAttributeIds: number[] = [];
    mandatoryAttributeIds.push(
      ...(playbook.nfConfig.trackerParameters.sampleAttributes
        .filter((i) => i.correctedMandatory || !!i.workflowParam)
        .map((i) => i.attributeId) || []),
    );
    return dispatch(loadSampleTypeDataForPlaybook(playbook.sampleType.id, uniq(mandatoryAttributeIds))).then(
      (response: Partial<ISampleEditingState>) => {
        const primaryParentType: ParentType = getPrimaryParentType(response.sampleType);

        if (!primaryParentType) {
          throw new Error(
            selMessage({
              id: "playbook.execution.error.missingPrimaryParent",
              values: { sampleTypeName: playbook.sampleType.name },
            }).toString(),
          );
        }
        return playbook;
      },
    );
  }, []);

  const onClose = useCallback(() => {
    props.onClose();
    // clear Playbook execution data
    dispatch(clearNfPlaybookExecutionData());
    // clear previous sample selection and all sample/parent editing inputs
    dispatch(clearSampleEditingData());
    // clear the involved forms (done by default anyway unless specified in the redux-form component
    // with {destroyOnUnmount: false}) so that we can go back and forth in the wizard )
    dispatch(destroy(NF_PLAYBOOK_SCREEN_IDS.NF_WORKFLOW_PARAMS_SCREEN));
  }, []);

  const steps = useMemo(() => {
    if (playbook?.type.id === PLAYBOOK_TYPE.PROCESSOR) {
      return [
        NF_PLAYBOOK_SCREEN_IDS.NF_BASICS_SCREEN,
        NF_PLAYBOOK_SCREEN_IDS.NF_PROCESSOR_PARAMS_SCREEN,
        //NF_PLAYBOOK_SCREEN_IDS.NF_PROCESSOR_REVIEW_SCREEN,
      ];
    }
    if (playbook?.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS) {
      return [
        NF_PLAYBOOK_SCREEN_IDS.NF_BASICS_SCREEN,
        NF_PLAYBOOK_SCREEN_IDS.NF_WORKFLOW_PARAMS_SCREEN,
        NF_PLAYBOOK_SCREEN_IDS.NF_REVIEW_SCREEN,
      ];
    }
    if (playbook?.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES) {
      return [
        NF_PLAYBOOK_SCREEN_IDS.NF_BASICS_SCREEN,
        NF_PLAYBOOK_SCREEN_IDS.NF_EDIT_SAMPLES_SCREEN,
        NF_PLAYBOOK_SCREEN_IDS.NF_WORKFLOW_PARAMS_SCREEN,
        NF_PLAYBOOK_SCREEN_IDS.NF_REVIEW_SCREEN,
      ];
    }
    if (playbook?.type.class === PLAYBOOK_CLASS.REPORT) {
      return [
        NF_PLAYBOOK_SCREEN_IDS.NF_BASICS_SCREEN,
        NF_PLAYBOOK_SCREEN_IDS.NF_GENOME_SELECTION_SCREEN,
        NF_PLAYBOOK_SCREEN_IDS.NF_WORKFLOW_PARAMS_SCREEN,
        NF_PLAYBOOK_SCREEN_IDS.NF_REVIEW_SCREEN,
      ];
    }
    if (playbook?.type.class === PLAYBOOK_CLASS.ALIGNMENT) {
      return [NF_PLAYBOOK_SCREEN_IDS.NF_BASICS_SCREEN, NF_PLAYBOOK_SCREEN_IDS.NF_GENOME_SELECTION_SCREEN];
    }
    return [];
  }, [playbook]);

  const mapStepToScreen = useCallback((page: IPlaybookPage): IWizardStep => {
    switch (page?.pageIdentifier) {
      case NF_PLAYBOOK_SCREEN_IDS.NF_BASICS_SCREEN:
        return <NfPlaybookBasicsScreen playbookPage={page} />;
      case NF_PLAYBOOK_SCREEN_IDS.NF_EDIT_SAMPLES_SCREEN:
        return <NfPlaybookSamplesScreen playbookPage={page} />;
      case NF_PLAYBOOK_SCREEN_IDS.NF_WORKFLOW_PARAMS_SCREEN:
        return <NfPlaybookWorkflowParamsScreen playbookPage={page} />;
      case NF_PLAYBOOK_SCREEN_IDS.NF_GENOME_SELECTION_SCREEN:
        return <NfGenomeSelectionScreen playbookPage={page} />;
      case NF_PLAYBOOK_SCREEN_IDS.NF_REVIEW_SCREEN:
        return <NfPlaybookReviewScreen playbookPage={page} />;
      case NF_PLAYBOOK_SCREEN_IDS.NF_PROCESSOR_PARAMS_SCREEN:
        return <NfPlaybookProcessorParamsScreen playbookPage={page} />;
    }
  }, []);

  return (
    <PlaybookWizard
      title={playbook?.name}
      suffix={playbook?.nameSuffix}
      isLoading={isInitializingPlaybook}
      isInitializing={isInitializingPlaybook}
      //helpLink={this.getHelpLink()}
      className={"sample-registration-wizard"}
      steps={steps.map((step: NF_PLAYBOOK_SCREEN_IDS) => NF_PLAYBOOK_EXECUTION_SCREENS[step])}
      mapStepToScreen={mapStepToScreen}
      needsWideSpace={() => false}
      onClose={onClose}
      playbook={true}
      configurationError={configurationError}
      updateConfigurationError={setConfigurationError}
      icon={getIconElementForCustomPlaybook(playbook)}
    />
  );
};

export { NfPlaybookWizard };
